import { useAuth } from '../contexts/AuthContext';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { FolderOpen, Users, FileText, Clock } from 'lucide-react';
import PendingInvites from '../components/dashboard/PendingInvites';
import { orgRoleBadgeClass, formatOrgRoleLabel } from '../lib/orgRoles';

const DashboardPage = () => {
  const { user, currentOrg } = useAuth();
  const { fetchOrganizations } = useOutletContext();

  const stats = [
    {
      title: 'Documentation Spaces',
      value: '0',
      description: 'Active spaces',
      icon: FolderOpen,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    },
    {
      title: 'Team Members',
      value: currentOrg?.member_count || '0',
      description: 'In this workspace',
      icon: Users,
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
    },
    {
      title: 'Documents',
      value: '0',
      description: 'Total documents',
      icon: FileText,
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    },
    {
      title: 'Last Updated',
      value: 'Never',
      description: 'Recent activity',
      icon: Clock,
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    },
  ];

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold font-[Manrope] text-slate-900 dark:text-white">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          {currentOrg && (
            <Badge className={orgRoleBadgeClass(currentOrg.user_role)}>
              {formatOrgRoleLabel(currentOrg.user_role)}
            </Badge>
          )}
        </div>
        <p className="text-slate-600 dark:text-slate-400">
          {currentOrg ? (
            <>Here's what's happening in <span className="font-medium text-slate-900 dark:text-white">{currentOrg.name}</span></>
          ) : (
            'Select or create a workspace to get started'
          )}
        </p>
      </div>

      {/* Pending Invites */}
      <PendingInvites onInviteAccepted={() => fetchOrganizations()} />

      {currentOrg ? (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
              <Card
                key={index}
                className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                data-testid={`stat-card-${index}`}
              >
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <stat.icon className={`w-4 h-4 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">
                    {stat.value}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Getting Started */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
                Getting Started
              </CardTitle>
              <CardDescription>
                Set up your documentation workspace in a few steps
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-medium text-sm">1</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 dark:text-white mb-1">
                      Create your first space
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Spaces help you organize documentation by product, team, or topic.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                  <div className="w-8 h-8 rounded-full bg-slate-300 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <span className="text-slate-600 dark:text-slate-400 font-medium text-sm">2</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 dark:text-white mb-1">
                      Invite your team
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Collaborate with team members by inviting them to your workspace.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                  <div className="w-8 h-8 rounded-full bg-slate-300 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <span className="text-slate-600 dark:text-slate-400 font-medium text-sm">3</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 dark:text-white mb-1">
                      Start documenting
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Create beautiful documentation that your users will love.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              No Workspace Selected
            </h3>
            <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
              Select an existing workspace from the sidebar or create a new one to get started.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
